/**
 * ax-video.js
 * Custom element <ax-video-player> porte depuis le theme B2C
 * (Nutrimuscle : _dev/js/sections/ax-video.js).
 * Gere la lecture des videos MP4 uploadees ainsi que YouTube / Vimeo
 * (chargement des API a la demande), l'autoplay via IntersectionObserver,
 * et la pause quand l'onglet passe en arriere-plan.
 *
 * @class AxVideo
 * @extends HTMLElement
 */
class AxVideo extends HTMLElement {
	constructor() {
		super();
		this.isClickEventAttached = false;
	}

	get playing() {
		return this.hasAttribute('playing');
	}

	get player() {
		return (this.playerProxy =
			this.playerProxy ||
			new Proxy(this.getPlayer(), {
				get: (target, prop) => {
					return async () => {
						target = await target;
						this.playerHandler(target, prop);
					};
				},
			}));
	}

	static async loadScript(src, errorMessage) {
		return new Promise((resolve, reject) => {
			const script = document.createElement('script');
			script.src = src;
			script.onload = resolve;
			script.onerror = () => reject(new Error(errorMessage));
			document.head.appendChild(script);
		});
	}

	connectedCallback() {
		this.abortController = new AbortController();

		this.handleVideoSrc();

		if (this.hasAttribute('autoplay')) {
			this.createObserver();
		} else if (!this.querySelector('template') && this.querySelector('iframe')) {
			this.play();
		} else if (!this.isClickEventAttached) {
			this.addEventListener('click', this.play.bind(this), {
				once: true,
				signal: this.abortController.signal,
			});
			this.isClickEventAttached = true;
		}

		document.addEventListener('visibilitychange', this.visibilityChangeHandler.bind(this));
	}

	disconnectedCallback() {
		this.abortController.abort();
		this.removeEventListener('click', this.play);
		document.removeEventListener('visibilitychange', this.visibilityChangeHandler.bind(this));
		if (this.observer) {
			this.observer.disconnect();
			this.observer = null;
		}
	}

	handleVideoSrc() {
		const videoElement = this.querySelector('video');
		if (!videoElement) return;
		const videoSrc = videoElement.getAttribute('data-src');
		const videoSrcMobile = videoElement.getAttribute('data-mobile-src');

		if (videoSrc) {
			videoElement.src = window.innerWidth < 750 && videoSrcMobile ? videoSrcMobile : videoSrc;
			videoElement.removeAttribute('data-src');
			videoElement.removeAttribute('data-mobile-src');
		}
	}

	play() {
		if (!this.playing) this.player.play();
	}

	pause() {
		if (this.playing) this.player.pause();
	}

	visibilityChangeHandler() {
		document.hidden ? this.pause() : this.play();
	}

	getPlayer() {
		if (this.hasAttribute('host')) {
			this.setAttribute('loaded', '');
			return new Promise(async (resolve, reject) => await this.loadExternalPlayer(resolve, reject));
		}
		const videoElement = this.querySelector('video');
		this.setAttribute('loaded', '');
		this.setupHTMLPlayer(videoElement);
		return videoElement;
	}

	async loadExternalPlayer(resolve, reject) {
		try {
			const templateElement = this.querySelector('template');
			if (templateElement) {
				templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true));
			}

			const muteVideo =
				this.hasAttribute('autoplay') || window.matchMedia('screen and (max-width: 992px)').matches;

			if (this.getAttribute('host') === 'youtube') {
				if (!window.YT || !window.YT.Player) {
					await AxVideo.loadScript('https://www.youtube.com/iframe_api', 'Failed to load YouTube script');
				}

				await new Promise((res) => (window.onYouTubeIframeAPIReady = () => res()));

				return this.setupYouTubePlayer(resolve, muteVideo);
			}

			if (this.getAttribute('host') === 'vimeo') {
				if (!window.Vimeo || !window.Vimeo.Player) {
					await AxVideo.loadScript('https://player.vimeo.com/api/player.js', 'Failed to load Vimeo script');
				}

				return this.setupVimeoPlayer(resolve, muteVideo);
			}
		} catch (error) {
			console.error('An error occurred while creating the video player:', error.message);
			reject(error);
		}
	}

	setupYouTubePlayer(resolve, muteVideo) {
		const iframe = this.querySelector('iframe');
		const showControls = this.hasAttribute('data-show-controls');

		const player = new YT.Player(iframe, {
			playerVars: {
				controls: showControls ? 1 : 0,
			},
			events: {
				onReady: () => {
					if (muteVideo) {
						player.mute();
					}
					resolve(player);
				},
				onStateChange: (event) => {
					if (event.data === YT.PlayerState.PLAYING) {
						this.setAttribute('playing', '');
					} else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) {
						this.removeAttribute('playing');
					}
				},
			},
		});
	}

	setupVimeoPlayer(resolve, muteVideo) {
		const iframe = this.querySelector('iframe');
		const player = new Vimeo.Player(iframe);

		if (muteVideo) player.setMuted(true);

		player.on('play', () => this.setAttribute('playing', ''));
		player.on('pause', () => this.removeAttribute('playing'));
		player.on('ended', () => this.removeAttribute('playing'));

		resolve(player);
	}

	setupHTMLPlayer(videoElement) {
		videoElement.addEventListener('play', () => this.setAttribute('playing', ''));

		videoElement.addEventListener('pause', () => {
			if (!videoElement.seeking && videoElement.paused) this.removeAttribute('playing');
		});
	}

	playerHandler(target, prop) {
		if (this.getAttribute('host') === 'youtube') {
			prop === 'play' ? target.playVideo() : target.pauseVideo();
		} else {
			target[prop]();
		}
	}

	createObserver() {
		this.observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => (entry.isIntersecting ? this.play() : this.pause()));
		});

		this.observer.observe(this);
	}
}

if (!customElements.get('ax-video-player')) {
	customElements.define('ax-video-player', AxVideo);
}
